import { db } from '../firebaseFunctions/config';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { isDemoMode } from '../demo/isDemoMode';
import demoStore from '../demo/demoStore';

/**
 * Fetch all confirmed vendors for an event.
 * "Confirmed" = applicationStatus Approved/Registered + paymentStatus paid.
 */
export const getEventVendors = async (eventId) => {
  if (isDemoMode) {
    const all = demoStore.getDocs('mpsEventRegistration', [
      { type: 'where', field: 'eventId', op: '==', value: eventId },
    ]);
    const vendors = all
      .filter(v =>
        ['approved', 'registered'].includes(v.data.applicationStatus?.toLowerCase()) &&
        v.data.paymentStatus?.toLowerCase() === 'paid'
      )
      .map(v => ({ id: v.id, ...v.data }));
    return { success: true, vendors };
  }

  try {
    const q = query(
      collection(db, 'mpsEventRegistration'),
      where('eventId', '==', eventId),
      where('paymentStatus', '==', 'paid')
    );
    const snap = await getDocs(q);
    const vendors = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(v => ['approved', 'registered'].includes(v.applicationStatus?.toLowerCase()));
    return { success: true, vendors };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

/**
 * Check in a vendor by their applicationId (mpsEventRegistration doc ID).
 */
export const checkInVendor = async ({ applicationId, eventId }) => {
  if (isDemoMode) {
    const existing = demoStore.getDoc('mpsEventRegistration', applicationId);
    if (!existing) return { success: false, error: 'Vendor not found' };
    if (existing.eventId !== eventId) return { success: false, error: 'QR code is for a different event' };
    if (!['approved', 'registered'].includes(existing.applicationStatus?.toLowerCase())) {
      return { success: false, error: 'Vendor is not confirmed for this event' };
    }
    if (existing.vendorCheckedIn) {
      return { success: true, alreadyCheckedIn: true, businessName: existing.businessName, boothNumber: existing.boothNumber };
    }
    demoStore.updateDoc('mpsEventRegistration', applicationId, {
      vendorCheckedIn: true,
      vendorCheckedInAt: new Date(),
    });
    return { success: true, alreadyCheckedIn: false, businessName: existing.businessName, boothNumber: existing.boothNumber };
  }

  try {
    const docRef = doc(db, 'mpsEventRegistration', applicationId);
    const { getDoc } = await import('firebase/firestore');
    const snap = await getDoc(docRef);
    if (!snap.exists()) return { success: false, error: 'Vendor not found' };

    const data = snap.data();
    if (data.eventId !== eventId) return { success: false, error: 'QR code is for a different event' };
    if (!['approved', 'registered'].includes(data.applicationStatus?.toLowerCase())) {
      return { success: false, error: 'Vendor is not confirmed for this event' };
    }
    if (data.vendorCheckedIn) {
      return { success: true, alreadyCheckedIn: true, businessName: data.businessName, boothNumber: data.boothNumber };
    }

    await updateDoc(docRef, { vendorCheckedIn: true, vendorCheckedInAt: serverTimestamp() });
    return { success: true, alreadyCheckedIn: false, businessName: data.businessName, boothNumber: data.boothNumber };
  } catch (err) {
    return { success: false, error: err.message };
  }
};
